// shared/client-reference.js — the client’s own reference for a piece of
// work: their purchase order, job or ticket number (feature sketch 1,
// docs/designs/feature-sketches.html).
//
// Optional on an invoice, a quote and a recurring schedule, whose invoices
// each carry the schedule’s. It prints as “Your reference” beside the dates,
// only when there is one, and an e-invoice states it as the buyer reference
// (BT-10). A credit note shows its invoice’s.
//
// A client can need one on every invoice (`requiresReference` on the
// contact): facilities managers and main contractors often won’t pay an
// invoice without their PO. Then no invoice goes to them without it, and every
// place that could send one refuses in the same words: the send itself
// (server/invoice-send.js, which a scheduled send and a phone’s queued send
// both reach), a recurring run, starting a schedule, and the compose screens
// on the web and the phone, which show the sentence on the field.
//
// Pure: no imports, no DOM, no node builtins, so the server, the web, the demo
// and the phone (through shared/ios-facade.js) all ask the one rule.

// FIFTY CHARACTERS. A purchase order or job number is rarely more than twenty;
// fifty leaves room for one that names a site or a block as well
// (“WO-2026-004471 / Block C, Riverside”) while it still sits beside the dates
// on the document in no more than two lines. And it is well inside what the
// accounting packages’ own reference fields hold (Xero’s takes 255), so a sync
// to one never has to cut it short.
export const CLIENT_REFERENCE_MAX_LENGTH = 50;

/** Every word the field and the client setting say, so both clients say them alike. */
export const CLIENT_REFERENCE_COPY = {
  label: "Client’s reference",
  hint: "Their purchase order or job number, if they gave you one.",
  // On the document: the client is reading, and it’s their number.
  onDocument: "Your reference",
  requiresLabel: "Needs a reference on every invoice",
  requiresHint: "Sendinvo won’t send them an invoice without one.",
  tooLong: `Shorten the reference to ${CLIENT_REFERENCE_MAX_LENGTH} characters or fewer.`
};

/**
 * A reference as it is stored: one line, its ends trimmed, any run of spaces,
 * tabs or line breaks inside it made one space. "" for none.
 */
export function normaliseClientReference(raw) {
  if (raw == null) return "";
  return String(raw).replace(/\s+/g, " ").trim();
}

/**
 * A reference somebody typed, as stored, or the sentence refusing it.
 * `{ value }` is null for none, which is what an empty field means.
 */
export function checkClientReference(raw) {
  const value = normaliseClientReference(raw);
  if (!value) return { value: null };
  // Counted in characters as a person counts them, not UTF-16 units: an
  // accented letter or an emoji is one.
  if ([...value].length > CLIENT_REFERENCE_MAX_LENGTH) return { error: CLIENT_REFERENCE_COPY.tooLong };
  return { value };
}

/**
 * The reference a save of a document carries. A body without the key keeps
 * `stored`: an app build from before the field saves a draft without it, and
 * must not take somebody’s reference off by not knowing it was there.
 */
export function clientReferenceFromBody(body, stored = null) {
  if (!body || !Object.prototype.hasOwnProperty.call(body, "clientReference")) {
    return { value: normaliseClientReference(stored) || null };
  }
  return checkClientReference(body.clientReference);
}

// "Hallam & Pryce" → "Hallam & Pryce’s". A name ending in s takes the
// apostrophe alone, "Ashworth Developments’", "Halliwell & Sons’": a client is
// usually a business, and its name usually ends in a plural ("Sons’s" reads as
// a typo). One that already ends in a possessive ("Sainsbury’s") is left as it
// is, and a trailing full stop ("Acme Ltd.") goes, so it never reads "Ltd.’s".
export function possessive(name) {
  const plain = String(name || "").trim().replace(/\.+$/, "").trim();
  if (!plain) return "the client’s";
  if (/['’]s$/i.test(plain)) return plain.replace(/'s$/i, "’s");
  if (/s$/i.test(plain)) return `${plain}’`;
  return `${plain}’s`;
}

/**
 * What a send refused for want of a reference says, naming the client as the
 * Clients list shows them: “Enter Hallam & Pryce’s reference. They need one on
 * every invoice.”
 */
export function referenceRequiredSentence(clientName) {
  const whose = possessive(clientName);
  return `Enter ${whose} reference. They need one on every invoice.`;
}

/**
 * Whether an invoice may go to its client as far as the reference goes: "" when
 * it may, else the sentence. Only an invoice is asked: a quote is not a request
 * for payment, and the setting is about invoices.
 */
export function missingClientReference({ requiresReference = false, clientReference = "", clientName = "" } = {}) {
  if (!requiresReference) return "";
  return normaliseClientReference(clientReference) ? "" : referenceRequiredSentence(clientName);
}

/**
 * Everything wrong with a reference before a document can go out, in one
 * pass: too long for any kind, missing for an invoice (or a schedule starting,
 * whose every run is one) to a client that needs one. [] when nothing is.
 */
export function clientReferenceRefusals({ kind = "invoice", clientReference = "", requiresReference = false, clientName = "" } = {}) {
  const checked = checkClientReference(clientReference);
  if (checked.error) return [checked.error];
  if (kind !== "invoice" && kind !== "schedule") return [];
  const missing = missingClientReference({ requiresReference, clientReference: checked.value || "", clientName });
  return missing ? [missing] : [];
}
